import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

export interface Milestone {
  _id?: string;
  id?: string;
  projectId: string;
  title: string;
  description?: string;
  scheduledDate: string | Date;
  completedDate?: string | Date;
  status: 'pending' | 'in_progress' | 'completed' | 'delayed';
  delayReason?: string;
  earlyCompletion?: boolean;
  earlyCompletionReason?: string;
  createdBy?: {
    _id: string;
    displayName: string;
    email: string;
  };
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

interface MilestonesState {
  items: Record<string, Milestone>;
  projectMilestones: Record<string, string[]>; // projectId -> milestone IDs
  isLoading: boolean;
  error: string | null;
}

const initialState: MilestonesState = {
  items: {},
  projectMilestones: {},
  isLoading: false,
  error: null,
};

// Fetch milestones for a project
export const fetchProjectMilestones = createAsyncThunk(
  'milestones/fetchProjectMilestones',
  async ({ projectId, includeDeleted = false }: { projectId: string; includeDeleted?: boolean }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const config = state.auth.accessToken
      ? { 
          headers: { Authorization: `Bearer ${state.auth.accessToken}` },
          params: includeDeleted ? { includeDeleted: 'true' } : {}
        }
      : {};
    const response = await axios.get(`${API_BASE_URL}/milestones/project/${projectId}`, config);
    return { projectId, milestones: response.data.data.milestones };
  }
);

// Fetch single milestone
export const fetchMilestoneById = createAsyncThunk(
  'milestones/fetchMilestoneById',
  async (milestoneId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const config = state.auth.accessToken
      ? { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
      : {};
    const response = await axios.get(`${API_BASE_URL}/milestones/${milestoneId}`, config);
    return response.data.data.milestone;
  }
);

// Create milestone
export const createMilestone = createAsyncThunk(
  'milestones/create',
  async (data: Partial<Milestone>, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(`${API_BASE_URL}/milestones`, data, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return response.data.data.milestone;
  }
);

// Update milestone
export const updateMilestone = createAsyncThunk(
  'milestones/update',
  async ({ id, data }: { id: string; data: Partial<Milestone> }, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.put(`${API_BASE_URL}/milestones/${id}`, data, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return response.data.data.milestone;
  }
);

// Delete milestone
export const deleteMilestone = createAsyncThunk(
  'milestones/delete',
  async (id: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    await axios.delete(`${API_BASE_URL}/milestones/${id}`, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return id;
  }
);

// Restore milestone
export const restoreMilestone = createAsyncThunk(
  'milestones/restore',
  async (id: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(`${API_BASE_URL}/milestones/${id}/restore`, {}, {
      headers: { Authorization: `Bearer ${state.auth.accessToken}` },
    });
    return response.data.data.milestone;
  }
);

const milestonesSlice = createSlice({
  name: 'milestones',
  initialState,
  reducers: {
    clearMilestones: (state) => {
      state.items = {};
      state.projectMilestones = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectMilestones.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectMilestones.fulfilled, (state, action) => {
        state.isLoading = false;
        const milestoneIds: string[] = [];
        action.payload.milestones.forEach((milestone: Milestone) => {
          const id = milestone._id || milestone.id || '';
          if (id) {
            state.items[id] = milestone;
            milestoneIds.push(id);
          }
        });
        state.projectMilestones[action.payload.projectId] = milestoneIds;
      })
      .addCase(fetchProjectMilestones.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch milestones';
      })
      .addCase(fetchMilestoneById.fulfilled, (state, action) => {
        const id = action.payload._id || action.payload.id || '';
        if (id) {
          state.items[id] = action.payload;
        }
      })
      .addCase(createMilestone.fulfilled, (state, action) => {
        const id = action.payload._id || action.payload.id || '';
        if (id) {
          state.items[id] = action.payload;
          const projectId = action.payload.projectId;
          if (projectId && !state.projectMilestones[projectId]) {
            state.projectMilestones[projectId] = [];
          }
          if (projectId && state.projectMilestones[projectId]) {
            state.projectMilestones[projectId].push(id);
          }
        }
      })
      .addCase(updateMilestone.fulfilled, (state, action) => {
        const id = action.payload._id || action.payload.id || '';
        if (id) {
          state.items[id] = action.payload;
        }
      })
      .addCase(deleteMilestone.fulfilled, (state, action) => {
        const id = action.payload;
        // Mark as deleted instead of removing
        if (state.items[id]) {
          state.items[id] = { ...state.items[id], deletedAt: new Date().toISOString() };
        }
      })
      .addCase(restoreMilestone.fulfilled, (state, action) => {
        const id = action.payload._id || action.payload.id || '';
        if (id) {
          // Remove deletedAt to restore
          const restored = { ...action.payload };
          delete restored.deletedAt;
          state.items[id] = restored;
        }
      });
  },
});

export const { clearMilestones } = milestonesSlice.actions;
export default milestonesSlice.reducer;

